import { useState, useCallback } from 'react'
import { Plus, Eye, EyeOff, Pencil, Trash2, KeyRound, Copy, Check } from 'lucide-react'
import { useSecretsList, useSecretValue, useCreateSecret, useUpdateSecret, useDeleteSecret } from '@/hooks/useSecrets'
import { Button, Modal, Skeleton, EmptyState } from '@/components/ui'
import { toast } from 'sonner'

// ── Types ─────────────────────────────────────────────────────────────────

interface SecretFormData {
  key: string
  value: string
  description: string
}

// ── Secrets Page ──────────────────────────────────────────────────────────

export default function SecretsPage() {
  const { data: secrets = [], isLoading } = useSecretsList()
  const createSecret = useCreateSecret()
  const updateSecret = useUpdateSecret()
  const deleteSecret = useDeleteSecret()

  const [addOpen, setAddOpen] = useState(false)
  const [editKey, setEditKey] = useState<string | null>(null)
  const [deleteKey, setDeleteKey] = useState<string | null>(null)
  const [revealedKey, setRevealedKey] = useState<string | null>(null)

  const handleCreate = useCallback(async (data: SecretFormData) => {
    try {
      await createSecret.mutateAsync(data)
      toast.success(`Secret "${data.key}" created`)
      setAddOpen(false)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to create secret')
    }
  }, [createSecret])

  const handleUpdate = useCallback(async (data: SecretFormData) => {
    if (!editKey) return
    try {
      const updates: { value?: string; description?: string } = { description: data.description }
      if (data.value) updates.value = data.value
      await updateSecret.mutateAsync({ key: editKey, data: updates })
      toast.success(`Secret "${editKey}" updated`)
      setEditKey(null)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to update secret')
    }
  }, [editKey, updateSecret])

  const handleDelete = useCallback(async () => {
    if (!deleteKey) return
    try {
      await deleteSecret.mutateAsync(deleteKey)
      toast.success(`Secret "${deleteKey}" deleted`)
      setDeleteKey(null)
      if (revealedKey === deleteKey) setRevealedKey(null)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to delete secret')
    }
  }, [deleteKey, deleteSecret, revealedKey])

  const editTarget = editKey ? secrets.find(s => s.key === editKey) : null

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* Header */}
      <div style={{
        padding: '16px 24px',
        borderBottom: '1px solid var(--border-subtle)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexShrink: 0,
      }}>
        <div>
          <h1 style={{ fontSize: 18, fontWeight: 700, color: 'var(--text-primary)', margin: 0 }}>
            Platform Secrets
          </h1>
          <p style={{ fontSize: 12, color: 'var(--text-tertiary)', margin: '4px 0 0 0' }}>
            Manage global secrets and API keys used across the platform.
          </p>
        </div>
        <Button variant="primary" size="md" icon={<Plus size={16} />} onClick={() => setAddOpen(true)}>
          Add Secret
        </Button>
      </div>

      {/* Content */}
      <div style={{ flex: 1, overflow: 'auto', padding: 24 }}>
        {isLoading ? (
          <SecretsTableSkeleton />
        ) : secrets.length === 0 ? (
          <EmptyState
            icon={<KeyRound size={48} />}
            title="No secrets yet"
            description="Platform secrets are global key-value pairs used for API keys, tokens, and other sensitive configuration."
            action={{ label: 'Add Secret', onClick: () => setAddOpen(true), icon: <Plus size={16} /> }}
          />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {secrets.map((secret: any) => (
              <SecretRow
                key={secret.key}
                secretKey={secret.key}
                description={secret.description}
                isNotSet={!secret.is_set}
                isRevealed={revealedKey === secret.key}
                onToggleReveal={() => setRevealedKey(prev => prev === secret.key ? null : secret.key)}
                onEdit={() => setEditKey(secret.key)}
                onDelete={() => setDeleteKey(secret.key)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Add Modal */}
      <SecretFormModal
        isOpen={addOpen}
        title="Add Secret"
        subtitle="Create a new platform secret."
        onClose={() => setAddOpen(false)}
        onSubmit={handleCreate}
        isSubmitting={createSecret.isPending}
        submitLabel="Create"
      />

      {/* Edit Modal */}
      <SecretFormModal
        isOpen={!!editKey}
        title="Edit Secret"
        subtitle={`Update "${editKey}"`}
        onClose={() => setEditKey(null)}
        onSubmit={handleUpdate}
        isSubmitting={updateSecret.isPending}
        submitLabel="Save"
        initialData={editTarget ? { key: editTarget.key, value: '', description: editTarget.description } : undefined}
        keyReadOnly
      />

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={!!deleteKey}
        onClose={() => setDeleteKey(null)}
        title="Delete Secret"
        subtitle={`Are you sure you want to delete "${deleteKey}"? This action cannot be undone.`}
        footer={
          <>
            <Button variant="secondary" onClick={() => setDeleteKey(null)}>Cancel</Button>
            <Button variant="danger" onClick={handleDelete} disabled={deleteSecret.isPending}>
              {deleteSecret.isPending ? 'Deleting...' : 'Delete'}
            </Button>
          </>
        }
      >
        <p style={{ fontSize: 13, color: 'var(--text-secondary)', margin: 0, lineHeight: 1.5 }}>
          This will permanently remove the secret <strong style={{ fontFamily: 'var(--font-mono)' }}>{deleteKey}</strong> from the platform.
          Any services relying on this secret will lose access.
        </p>
      </Modal>
    </div>
  )
}

// ── Secret Row ────────────────────────────────────────────────────────────

function SecretRow({
  secretKey,
  description,
  isNotSet,
  isRevealed,
  onToggleReveal,
  onEdit,
  onDelete,
}: {
  secretKey: string
  description: string
  isNotSet: boolean
  isRevealed: boolean
  onToggleReveal: () => void
  onEdit: () => void
  onDelete: () => void
}) {
  return (
    <div style={{
      padding: '14px 20px',
      backgroundColor: 'var(--bg-secondary)',
      borderRadius: 'var(--radius-md)',
      border: '1px solid var(--border-subtle)',
      display: 'flex',
      alignItems: 'center',
      gap: 16,
    }}>
      {/* Left: Key + Description stacked */}
      <div style={{ flex: '0 0 280px', minWidth: 0 }}>
        <div style={{ fontFamily: 'var(--font-mono)', fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>
          {secretKey}
        </div>
        {description && (
          <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 2, lineHeight: 1.3 }}>
            {description}
          </div>
        )}
      </div>

      {/* Middle: Value with inline reveal/edit/copy */}
      <div style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 6 }}>
        {isNotSet ? (
          <button onClick={onEdit} style={{
            background: 'none', border: '1px dashed var(--status-warning)', borderRadius: 'var(--radius-sm)',
            cursor: 'pointer', padding: '4px 10px', display: 'inline-flex', alignItems: 'center', gap: 6,
            fontSize: 12, color: 'var(--status-warning)', fontFamily: 'var(--font-sans)',
          }}>
            <Pencil size={12} />
            Not set — click to configure
          </button>
        ) : isRevealed ? (
          <RevealedValue secretKey={secretKey} onHide={onToggleReveal} onEdit={onEdit} />
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontSize: 13, color: 'var(--text-tertiary)', letterSpacing: '0.1em' }}>••••••••••••</span>
            <button onClick={onToggleReveal} className="btn-ghost" title="Reveal value" style={{
              background: 'none', border: 'none', cursor: 'pointer', padding: 2, display: 'flex', color: 'var(--text-tertiary)',
            }}>
              <Eye size={14} />
            </button>
            <button onClick={onEdit} className="btn-ghost" title="Edit" style={{
              background: 'none', border: 'none', cursor: 'pointer', padding: 2, display: 'flex', color: 'var(--text-tertiary)',
            }}>
              <Pencil size={14} />
            </button>
          </div>
        )}
      </div>

      {/* Right: Delete (subtle) */}
      <button onClick={onDelete} className="btn-ghost" title="Delete" style={{
        background: 'none', border: 'none', cursor: 'pointer', padding: 4, display: 'flex',
        color: 'var(--text-tertiary)', opacity: 0.5, flexShrink: 0,
      }}>
        <Trash2 size={14} />
      </button>
    </div>
  )
}

// ── Revealed Value ────────────────────────────────────────────────────────

function RevealedValue({ secretKey, onHide, onEdit }: { secretKey: string; onHide: () => void; onEdit?: () => void }) {
  const { data, isLoading } = useSecretValue(secretKey, true)
  const [copied, setCopied] = useState(false)

  const handleCopy = useCallback(() => {
    if (!data?.value) return
    navigator.clipboard.writeText(data.value)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }, [data?.value])

  if (isLoading) {
    return <Skeleton width={160} height={16} />
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <code style={{
        fontSize: 12,
        fontFamily: 'var(--font-mono)',
        color: 'var(--text-primary)',
        backgroundColor: 'var(--bg-tertiary)',
        padding: '2px 8px',
        borderRadius: 'var(--radius-sm)',
        maxWidth: 280,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      }}>
        {data?.value ?? ''}
      </code>
      <button
        onClick={handleCopy}
        className="btn-ghost"
        title="Copy to clipboard"
        style={{
          background: 'none', border: 'none', cursor: 'pointer',
          padding: 2, display: 'flex', alignItems: 'center',
          color: copied ? 'var(--status-healthy)' : 'var(--text-tertiary)',
        }}
      >
        {copied ? <Check size={14} /> : <Copy size={14} />}
      </button>
      {onEdit && (
        <button onClick={onEdit} className="btn-ghost" title="Edit" style={{
          background: 'none', border: 'none', cursor: 'pointer',
          padding: 2, display: 'flex', alignItems: 'center',
          color: 'var(--text-tertiary)',
        }}>
          <Pencil size={14} />
        </button>
      )}
      <button onClick={onHide} className="btn-ghost" title="Hide value" style={{
        background: 'none', border: 'none', cursor: 'pointer',
        padding: 2, display: 'flex', alignItems: 'center',
        color: 'var(--text-tertiary)',
      }}>
        <EyeOff size={14} />
      </button>
    </div>
  )
}

// ── Secret Form Modal ─────────────────────────────────────────────────────

function SecretFormModal({
  isOpen,
  title,
  subtitle,
  onClose,
  onSubmit,
  isSubmitting,
  submitLabel,
  initialData,
  keyReadOnly,
}: {
  isOpen: boolean
  title: string
  subtitle?: string
  onClose: () => void
  onSubmit: (data: SecretFormData) => void
  isSubmitting: boolean
  submitLabel: string
  initialData?: SecretFormData
  keyReadOnly?: boolean
}) {
  const [key, setKey] = useState('')
  const [value, setValue] = useState('')
  const [description, setDescription] = useState('')

  // Reset form when modal opens
  const handleClose = () => {
    setKey('')
    setValue('')
    setDescription('')
    onClose()
  }

  // Sync initial data when modal opens
  const [lastOpen, setLastOpen] = useState(false)
  if (isOpen && !lastOpen) {
    setKey(initialData?.key ?? '')
    setValue(initialData?.value ?? '')
    setDescription(initialData?.description ?? '')
  }
  if (isOpen !== lastOpen) setLastOpen(isOpen)

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    onSubmit({ key, value, description })
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={title}
      subtitle={subtitle}
      footer={
        <>
          <Button variant="secondary" onClick={handleClose}>Cancel</Button>
          <Button variant="primary" onClick={() => onSubmit({ key, value, description })} disabled={isSubmitting || (!keyReadOnly && !key)}>
            {isSubmitting ? 'Saving...' : submitLabel}
          </Button>
        </>
      }
    >
      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
        {/* Key */}
        <FormField label="Key" description="Unique identifier for this secret (e.g., OPENAI_API_KEY).">
          <input
            className="input-field"
            type="text"
            value={key}
            onChange={e => setKey(e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, ''))}
            placeholder="MY_SECRET_KEY"
            readOnly={keyReadOnly}
            style={{
              width: '100%',
              padding: '9px 12px',
              backgroundColor: keyReadOnly ? 'var(--bg-primary)' : 'var(--bg-tertiary)',
              border: '1px solid var(--border-subtle)',
              borderRadius: 'var(--radius-md)',
              fontSize: 13,
              color: 'var(--text-primary)',
              fontFamily: 'var(--font-mono)',
              outline: 'none',
              opacity: keyReadOnly ? 0.7 : 1,
            }}
          />
        </FormField>

        {/* Value */}
        <FormField label="Value" description={keyReadOnly ? 'Leave empty to keep the current value.' : 'The secret value.'}>
          <textarea
            className="input-field"
            value={value}
            onChange={e => setValue(e.target.value)}
            placeholder={keyReadOnly ? '(unchanged)' : 'Enter secret value'}
            rows={4}
            style={{
              width: '100%',
              padding: '9px 12px',
              backgroundColor: 'var(--bg-tertiary)',
              border: '1px solid var(--border-subtle)',
              borderRadius: 'var(--radius-md)',
              fontSize: 13,
              color: 'var(--text-primary)',
              fontFamily: 'var(--font-mono)',
              outline: 'none',
              resize: 'vertical',
              minHeight: 80,
            }}
          />
        </FormField>

        {/* Description */}
        <FormField label="Description" description="Optional note about what this secret is used for.">
          <input
            className="input-field"
            type="text"
            value={description}
            onChange={e => setDescription(e.target.value)}
            placeholder="e.g., OpenAI API key for tenant provisioning"
            style={{
              width: '100%',
              padding: '9px 12px',
              backgroundColor: 'var(--bg-tertiary)',
              border: '1px solid var(--border-subtle)',
              borderRadius: 'var(--radius-md)',
              fontSize: 13,
              color: 'var(--text-primary)',
              outline: 'none',
            }}
          />
        </FormField>
      </form>
    </Modal>
  )
}

// ── Form Field ────────────────────────────────────────────────────────────

function FormField({ label, description, children }: { label: string; description: string; children: React.ReactNode }) {
  return (
    <div>
      <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 4 }}>
        {label}
      </label>
      <p style={{ fontSize: 12, color: 'var(--text-tertiary)', margin: '0 0 8px 0', lineHeight: 1.4 }}>
        {description}
      </p>
      {children}
    </div>
  )
}

// ── Skeleton ──────────────────────────────────────────────────────────────

function SecretsTableSkeleton() {
  return (
    <div style={{
      border: '1px solid var(--border-subtle)',
      borderRadius: 'var(--radius-lg)',
      overflow: 'hidden',
    }}>
      <div style={{ backgroundColor: 'var(--bg-tertiary)', padding: '10px 16px', display: 'flex', gap: 16 }}>
        <Skeleton width={60} height={11} />
        <Skeleton width={60} height={11} />
        <Skeleton width={80} height={11} />
        <div style={{ flex: 1 }} />
        <Skeleton width={60} height={11} />
      </div>
      {Array.from({ length: 3 }).map((_, i) => (
        <div key={i} style={{ padding: '12px 16px', borderTop: '1px solid var(--border-subtle)', display: 'flex', gap: 16, alignItems: 'center' }}>
          <Skeleton width={140} height={16} />
          <Skeleton width={80} height={16} />
          <Skeleton width={200} height={16} style={{ flex: 1 }} />
          <Skeleton width={60} height={28} />
        </div>
      ))}
    </div>
  )
}
